/**
 * GeoNames Service for City Autocomplete
 * Uses the free GeoNames API (http://www.geonames.org/)
 * No API key required, completely open source
 */

export interface CityResult {
  id: string;
  name: string;
  adminName1: string; // Province/State
  countryName: string;
  countryCode: string;
  lat: string;
  lng: string;
  population?: number;
}

export interface GeoNamesResponse {
  geonames: Array<{
    geonameId: number;
    name: string;
    adminName1: string;
    countryName: string;
    countryCode: string;
    lat: string;
    lng: string;
    population?: number;
    fcode: string;
  }>;
}

// Fallback Canadian cities for when API is unavailable
const CANADIAN_CITIES = [
  { name: 'Toronto', province: 'Ontario', country: 'Canada' },
  { name: 'Montreal', province: 'Quebec', country: 'Canada' },
  { name: 'Vancouver', province: 'British Columbia', country: 'Canada' },
  { name: 'Calgary', province: 'Alberta', country: 'Canada' },
  { name: 'Edmonton', province: 'Alberta', country: 'Canada' },
  { name: 'Ottawa', province: 'Ontario', country: 'Canada' },
  { name: 'Winnipeg', province: 'Manitoba', country: 'Canada' },
  { name: 'Quebec City', province: 'Quebec', country: 'Canada' },
  { name: 'Hamilton', province: 'Ontario', country: 'Canada' },
  { name: 'Kitchener', province: 'Ontario', country: 'Canada' },
  { name: 'London', province: 'Ontario', country: 'Canada' },
  { name: 'Victoria', province: 'British Columbia', country: 'Canada' },
  { name: 'Halifax', province: 'Nova Scotia', country: 'Canada' },
  { name: 'Oshawa', province: 'Ontario', country: 'Canada' },
  { name: 'Windsor', province: 'Ontario', country: 'Canada' },
  { name: 'Saskatoon', province: 'Saskatchewan', country: 'Canada' },
  { name: 'Regina', province: 'Saskatchewan', country: 'Canada' },
  { name: 'Burnaby', province: 'British Columbia', country: 'Canada' },
  { name: 'Richmond', province: 'British Columbia', country: 'Canada' },
  { name: 'Markham', province: 'Ontario', country: 'Canada' },
  { name: 'Vaughan', province: 'Ontario', country: 'Canada' },
  { name: 'Gatineau', province: 'Quebec', country: 'Canada' },
  { name: 'Longueuil', province: 'Quebec', country: 'Canada' },
  { name: 'Burnaby', province: 'British Columbia', country: 'Canada' },
  { name: 'Laval', province: 'Quebec', country: 'Canada' }
];

class GeoNamesService {
  private readonly baseUrl = 'https://secure.geonames.org';
  private readonly username = 'demo'; // Using demo username

  /**
   * Search local Canadian cities as fallback
   */
  private searchLocalCities(query: string, maxRows: number = 10): CityResult[] {
    const lowerQuery = query.toLowerCase();
    return CANADIAN_CITIES
      .filter(city => city.name.toLowerCase().includes(lowerQuery))
      .slice(0, maxRows)
      .map((city, index) => ({
        id: `local-${index}`,
        name: city.name,
        adminName1: city.province,
        countryName: city.country,
        countryCode: 'CA',
        lat: '0',
        lng: '0'
      }));
  }

  /**
   * Search for cities by name with autocomplete
   * @param query - The search query (city name)
   * @param maxRows - Maximum number of results (default: 10)
   * @param countryBias - Country code to prioritize (e.g., 'CA' for Canada)
   */
  async searchCities(
    query: string,
    maxRows: number = 10,
    countryBias?: string
  ): Promise<CityResult[]> {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const params = new URLSearchParams({
        q: query,
        maxRows: maxRows.toString(),
        username: this.username,
        type: 'json',
        featureClass: 'P', // Populated places (cities, towns, villages)
        style: 'medium',
        orderby: 'population' // Order by population for more relevant results
      });

      // If country bias is provided, add it to prioritize results from that country
      if (countryBias) {
        params.append('countryBias', countryBias);
      }

      const response = await fetch(`${this.baseUrl}/searchJSON?${params}`);

      if (!response.ok) {
        console.warn(`GeoNames API error: ${response.status}, falling back to local data`);
        return this.searchLocalCities(query, maxRows);
      }

      const data: GeoNamesResponse = await response.json();

      const results = data.geonames?.map(item => ({
        id: item.geonameId.toString(),
        name: item.name,
        adminName1: item.adminName1 || '',
        countryName: item.countryName,
        countryCode: item.countryCode,
        lat: item.lat,
        lng: item.lng,
        population: item.population
      })) || [];

      // If no results from API, fallback to local search
      if (results.length === 0) {
        return this.searchLocalCities(query, maxRows);
      }

      return results;
    } catch (error) {
      console.warn('Error fetching cities from GeoNames, using local fallback:', error);
      return this.searchLocalCities(query, maxRows);
    }
  }

  /**
   * Format a city result for display
   */
  formatCityDisplay(city: CityResult): string {
    const parts = [city.name];

    if (city.adminName1) {
      parts.push(city.adminName1);
    }

    parts.push(city.countryName);

    return parts.join(', ');
  }

  /**
   * Search specifically for Canadian cities (prioritized for government applications)
   */
  async searchCanadianCities(query: string, maxRows: number = 10): Promise<CityResult[]> {
    // First try local Canadian cities for immediate response
    const localResults = this.searchLocalCities(query, Math.min(maxRows, 5));

    // Then try API for additional results
    try {
      const apiResults = await this.searchCities(query, maxRows, 'CA');

      // Merge results, prioritizing local ones and avoiding duplicates
      const allResults = [...localResults];
      apiResults.forEach(apiCity => {
        if (!localResults.some(local =>
          local.name.toLowerCase() === apiCity.name.toLowerCase() &&
          local.adminName1.toLowerCase() === apiCity.adminName1.toLowerCase()
        )) {
          allResults.push(apiCity);
        }
      });

      return allResults.slice(0, maxRows);
    } catch (error) {
      console.warn('API unavailable, using local cities only:', error);
      return localResults;
    }
  }
}

export const geoNamesService = new GeoNamesService();
